// @owners { team: patient-health }
// eslint-disable-next-line import/no-deprecated
import { fetchInsurances } from '~shared/actions/insurances';
import { type ModalType, nextModalStep, openModal } from '~shared/actions/modal';
import { updateCashPay } from '~shared/actions/users';
import { getCardFinderInsurances } from '~shared/features/insurances/selectors/getCardFinderInsurances';
import { getInsurances } from '~shared/features/insurances/selectors/getInsurances';
import { getIsNeedsInsuranceForUser } from '~shared/features/insurances/selectors/getIsNeedsInsuranceForUser';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { getUserCashPayOnly } from '~shared/features/users/selectors/getUserCashPayOnly';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import {
  type ResolutionValues,
  getResolution,
} from '~shared/lib/analytics/src/events/onboarding/getInsuranceCompleted';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { connect } from '~shared/store';
import { type ReduxDispatchShared, type ReduxStateShared } from '~shared/types';
import { OnboardingInsuranceForm, type Props } from './OnboardingInsuranceForm';

const mapStateToProps = (state: ReduxStateShared) => {
  const {
    insurances: { createInsuranceSuccess, createPhotoInsuranceSuccess },
  } = state;
  const userID = getCurrentUserID(state);
  const { fetchInsurancesLoading } = getLoading(state);

  return {
    loading: fetchInsurancesLoading,
    insurances: getInsurances(state),
    cardFinderInsurances: getCardFinderInsurances(state),
    isNeedsInsurance: getIsNeedsInsuranceForUser(state, {
      userID,
    }),
    isCashPayOnly: getUserCashPayOnly(state, {
      userID,
    }),
    success: createInsuranceSuccess || createPhotoInsuranceSuccess,
    userID,
    resolution: getResolution(state),
  };
};

const mapDispatchToProps = (dispatch: ReduxDispatchShared) => {
  return {
    // eslint-disable-next-line import/no-deprecated
    fetchInsurances: () => dispatch(fetchInsurances()),
    updateCashPay: (userID: number, isCashPayOnly: boolean) => dispatch(updateCashPay(userID, isCashPayOnly)),
    openModal: (modalName: ModalType) => {
      dispatch(openModal(modalName));
    },
    trackCashPayOnlySet: () => {
      dispatch(
        sendAnalyticsEvent(
          createEvent(EVENTS.CASH_PAY_ONLY_SET, {
            source: 'onboarding',
            'new value': true,
          }),
        ),
      );
    },
    trackInsuranceStepCompleted: (cardFinderInsurancePresent: boolean, resolution: ResolutionValues) => {
      dispatch(
        sendAnalyticsEvent(
          createEvent(EVENTS.ONBOARDING__INSURANCE_STEP_COMPLETED, {
            'cardfinder insurance present': cardFinderInsurancePresent,
            resolution,
          }),
        ),
      );
    },
    next: () => {
      dispatch(nextModalStep('ONBOARDING_MODAL'));
    },
  };
};

const mergeProps = (
  stateProps: ReturnType<typeof mapStateToProps>,
  dispatchProps: ReturnType<typeof mapDispatchToProps>,
): Props => {
  const { userID, ...newStateProps } = stateProps;
  const { updateCashPay: updateUserCashPay, next, trackCashPayOnlySet, ...newDispatchProps } = dispatchProps;
  return {
    ...newStateProps,
    ...newDispatchProps,
    next,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    updateCashPay: async () => {
      const { success } = await updateUserCashPay(Number(userID), true);
      if (success) trackCashPayOnlySet();
      next();
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(OnboardingInsuranceForm);
